<template>
  <section
    :id="domain.anchor"
    class="domain-section"
    :class="{ 'domain-section--mobile': isMobile }"
    :data-tone="domain.tone"
  >
    <div class="domain-section__inner">
      <p class="domain-section__kicker">{{ eyebrow }}</p>
      <h2 class="domain-section__title">{{ domain.kicker }}</h2>

      <div class="domain-pills" role="tablist" :aria-label="domain.tabsLabel">
        <button
          v-for="(group, index) in domain.groups"
          :id="tabId(group.id)"
          :key="group.id"
          type="button"
          class="domain-pill"
          role="tab"
          :aria-selected="group.id === activeGroupId ? 'true' : 'false'"
          :aria-controls="panelId(group.id)"
          :tabindex="group.id === activeGroupId ? 0 : -1"
          @click="selectGroup(group.id)"
          @keydown="onPhraseKeydown($event, index)"
        >
          {{ group.label }}
        </button>
      </div>

      <div
        v-if="activeGroup"
        :id="panelId(activeGroup.id)"
        class="domain-section__panel"
        :data-count="activeGroup.features.length"
        role="tabpanel"
        :aria-labelledby="tabId(activeGroup.id)"
      >
        <Transition name="domain-panel" mode="out-in">
          <LandingFeatureGrid
            :key="activeGroup.id"
            :features="activeGroup.features"
            :compact="isMobile"
            stagger-transition
          />
        </Transition>
      </div>

      <div class="domain-closing">
        <h3>{{ closing.title }}</h3>
        <button type="button" class="domain-closing__cta" @click="onClosing">
          {{ closing.cta }}
        </button>
        <p :class="closing.privacy ? 'domain-closing__privacy' : 'domain-closing__meta'">
          <span v-if="closing.privacy" aria-hidden="true">🔒</span>
          {{ closing.note }}
        </p>
        <router-link class="domain-closing__more" :to="catalogTo">
          o ver todas las funcionalidades →
        </router-link>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import posthog from 'posthog-js'
import { useDisplay } from 'vuetify'
import LandingFeatureGrid from '@/components/home/landing/catalog/LandingFeatureGrid.vue'
import { landingCatalogTo } from '@/constants/landingCatalog'
import type { LandingDomain } from '@/types/landingCatalog.types'

const CLOSINGS = {
  escribir: {
    title: '¿Listo para tu primer capítulo?',
    cta: 'Empezar mi proyecto gratis',
    note: 'Sin registro · Novela, cuentos, poesía o guion',
    privacy: false,
  },
  compartir: {
    title: 'Escribe a solas, avanza en compañía',
    cta: 'Crear mi perfil de escritor',
    note: 'Compartes solo lo que eliges: tu manuscrito sigue privado.',
    privacy: true,
  },
} as const

export default defineComponent({
  name: 'HomeDomainSection',

  components: {
    LandingFeatureGrid,
  },

  props: {
    domain: {
      type: Object as PropType<LandingDomain>,
      required: true,
    },
  },

  emits: {
    'start-writing': () => true,
    'create-profile': () => true,
  },

  setup() {
    return {
      isMobile: useDisplay().mobile,
    }
  },

  data() {
    return {
      activeGroupId: '',
    }
  },

  computed: {
    activeGroup() {
      return this.domain.groups.find((group) => group.id === this.activeGroupId) ?? null
    },

    catalogTo() {
      return landingCatalogTo(this.domain.anchor)
    },

    eyebrow() {
      return this.domain.trackState === 'compartir' ? '👥 Para compartirla' : '✍️ Para escribirla'
    },

    closing() {
      return CLOSINGS[this.domain.trackState]
    },
  },

  created() {
    this.activeGroupId = this.domain.groups[0]?.id ?? ''
  },

  methods: {
    tabId(groupId: string) {
      return `landing-tab-${this.domain.id}-${groupId}`
    },

    panelId(groupId: string) {
      return `landing-panel-${this.domain.id}-${groupId}`
    },

    capture(event: string, properties: Record<string, string>) {
      if (!import.meta.env.VITE_POSTHOG_PROJECT_TOKEN || !import.meta.env.VITE_POSTHOG_HOST) return
      posthog.capture(event, properties)
    },

    heroState() {
      return document.getElementById('hero')?.dataset.state
    },

    selectGroup(groupId: string) {
      if (this.activeGroupId === groupId) return
      this.activeGroupId = groupId
      const properties: Record<string, string> = {
        path: this.domain.trackState,
        tab: groupId,
      }
      const heroState = this.heroState()
      if (heroState) properties.hero_state = heroState
      this.capture('path_tab_click', properties)
    },

    onClosing() {
      const properties: Record<string, string> = { path: this.domain.trackState }
      const heroState = this.heroState()
      if (heroState) properties.hero_state = heroState
      this.capture('path_cta', properties)
      if (this.domain.trackState === 'compartir') {
        this.$emit('create-profile')
        return
      }
      this.$emit('start-writing')
    },

    onPhraseKeydown(event: KeyboardEvent, index: number) {
      const count = this.domain.groups.length
      let next = index
      if (event.key === 'ArrowRight') next = (index + 1) % count
      else if (event.key === 'ArrowLeft') next = (index - 1 + count) % count
      else if (event.key === 'Home') next = 0
      else if (event.key === 'End') next = count - 1
      else return
      event.preventDefault()
      const group = this.domain.groups[next]
      if (!group) return
      this.selectGroup(group.id)
      this.$nextTick(() => {
        document.getElementById(this.tabId(group.id))?.focus()
      })
    },
  },
})
</script>

<style scoped>
.domain-section {
  box-sizing: border-box;
  scroll-margin-top: calc(var(--landing-nav-height, 4rem) + 60px);
  padding: 88px 0 96px;
  text-align: center;
  --accent: #c45a00;
  background: #fff3e4;
  color: #1d3557;
}

.domain-section[data-tone='cool'] {
  --accent: #0f6f86;
  background: #e4f6f8;
}

.domain-section__inner {
  width: min(1200px, 100%);
  margin: 0 auto;
  padding: 0 24px;
}

.domain-section__kicker {
  margin: 0 0 8px;
  color: var(--accent);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.domain-section__title {
  max-width: 820px;
  margin: 0 auto 28px;
  font-size: clamp(30px, 3.8vw, 48px);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.025em;
}

.domain-pills {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 34px;
}

.domain-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 2px solid #1d3557;
  border-radius: 999px;
  background: #fff;
  color: #1d3557;
  font: 700 16px inherit;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    transform 0.15s;
}

.domain-section[data-tone='cool'] .domain-pill {
  border-color: #0f6f86;
  color: #0f6f86;
}

.domain-pill:hover {
  transform: translateY(-2px);
}

.domain-section[data-tone='warm'] .domain-pill[aria-selected='true'] {
  border-color: #c45a00;
  background: #c45a00;
  color: #fff;
}

.domain-section[data-tone='cool'] .domain-pill[aria-selected='true'] {
  background: #0f6f86;
  color: #fff;
}

.domain-pill:focus-visible {
  outline: 3px solid var(--accent);
  outline-offset: 3px;
}

.domain-section__panel {
  min-height: 12rem;
  text-align: center;
}

.domain-section__panel[data-count='2'] :deep(.features-grid) {
  max-width: 720px;
  margin-inline: auto;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.domain-panel-enter-active,
.domain-panel-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.32s ease;
}

.domain-panel-enter-from,
.domain-panel-leave-to {
  opacity: 0;
}

.domain-panel-leave-to {
  transform: translateY(-8px);
}

.domain-panel-enter-from {
  transform: translateY(8px);
}

.domain-closing {
  max-width: 640px;
  margin: 44px auto 0;
  padding: 28px 28px 24px;
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 8px 30px rgba(29, 53, 87, 0.1);
}

.domain-closing h3 {
  margin: 0 0 14px;
  font-size: 22px;
}

.domain-closing__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  padding: 0 30px;
  border: 0;
  border-radius: 8px;
  background: #e06a00;
  color: #fff;
  font: 700 19px inherit;
  cursor: pointer;
}

.domain-section[data-tone='cool'] .domain-closing__cta {
  background: #0f6f86;
}

.domain-closing__cta:hover {
  background: #c45a00;
}

.domain-section[data-tone='cool'] .domain-closing__cta:hover {
  background: #0d3b66;
}

.domain-closing__cta:focus-visible {
  outline: 3px solid #1d3557;
  outline-offset: 3px;
}

.domain-closing__meta {
  margin: 12px 0 0;
  color: #5b6b7f;
  font-size: 14px;
}

.domain-closing__privacy {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 0;
  color: #0f6f86;
  font-size: 14px;
  font-weight: 600;
}

.domain-closing__more {
  display: inline-block;
  margin-top: 10px;
  color: inherit;
  font-size: 15px;
  font-weight: 700;
  text-decoration: none;
}

.domain-closing__more:focus-visible {
  outline: 3px solid var(--accent);
  outline-offset: 3px;
}

.domain-section--mobile {
  padding: 60px 0 68px;
}

.domain-section--mobile .domain-section__inner {
  padding: 0 18px;
}

.domain-section--mobile .domain-pills {
  gap: 6px;
}

.domain-section--mobile .domain-pill {
  gap: 6px;
  padding: 8px 12px;
  font-size: 14px;
}

.domain-section--mobile .domain-closing {
  padding: 24px 18px 20px;
}

.domain-section--mobile .domain-closing__cta {
  width: 100%;
  padding: 0 18px;
  font-size: 17px;
}

.domain-section--mobile .domain-section__panel[data-count='2'] :deep(.features-grid) {
  grid-template-columns: 1fr;
  max-width: none;
}

@media (prefers-reduced-motion: reduce) {
  .domain-pill,
  .domain-pill:hover,
  .domain-panel-enter-active,
  .domain-panel-leave-active {
    transition: none;
    transform: none;
  }

  .domain-panel-enter-from,
  .domain-panel-leave-to {
    opacity: 1;
    transform: none;
  }
}
</style>
